import { Component, Inject, inject } from '@angular/core';
import { AbstractStepConfigComponent } from '../abstract-step-config/abstract-step-config.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AbstractStepDTO, ConfiguratorTransactionalEmailService, EmailEventStepDTO, EngineTagService } from '@tj/tj-dmp-lib-api';
import { ON_CANCEL_TOKEN, ON_SAVE_TOKEN, PARTNER_TOKEN, READ_ONLY_MODE_TOKEN, STEP_TOKEN, TENANT_TOKEN } from '../injection-tokens';
import { takeUntil } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { StepDescriptionService } from '../step-description.service';

@Component({
    selector: 'dmp-email-event-step-config',
    templateUrl: './email-event-step-config.component.html',
    standalone: false
})
export class EmailEventStepConfigComponent extends AbstractStepConfigComponent {
  private readonly stepDescriptionService = inject(StepDescriptionService);

  emailEventForm: FormGroup;

  availableEvents: { label: string, value: EmailEventStepDTO.EmailEventsEnum }[] = [];
  availableTemplates: { label: string, value: string }[] = [];
  availableTags: { label: string, value: number }[] = [];
  availablbleLinks: { label: string, value: string }[] = [];

  get emailEventStep(): EmailEventStepDTO {
    return this.step as EmailEventStepDTO
  }

  get events() { return this.emailEventForm.get('events'); }
  get template() { return this.emailEventForm.get('template'); }
  get tags() { return this.emailEventForm.get('tags'); }
  get link() { return this.emailEventForm.get('link'); }

  constructor(
    @Inject(STEP_TOKEN) step: AbstractStepDTO,
    @Inject(TENANT_TOKEN) tenant: string,
    @Inject(PARTNER_TOKEN) partner: string,
    @Inject(READ_ONLY_MODE_TOKEN) readOnlyMode: string,
    @Inject(ON_SAVE_TOKEN) onSaveCallback: () => void,
    @Inject(ON_CANCEL_TOKEN) onCancelCallback: () => void,
    private formBuilder: FormBuilder,
    private emailService: ConfiguratorTransactionalEmailService,
    private tagService: EngineTagService,
  ) {
    super(step, tenant, partner, readOnlyMode, onSaveCallback, onCancelCallback);
  }

  protected initializeComponent(): void {
    this.createForm();
    this.setupFormSubscription();
    this.loadAvailableEvents();

    forkJoin({
      templates: this.emailService.getAllTransactionalEmailsUsingGET1(this.tenant),
      tags: this.tagService.getAllTags(this.tenant)
    }).subscribe({
      next: data => {
        this.availableTemplates = data.templates
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(template => ({ label: template.name, value: template.id }));
        this.availableTags = data.tags
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(tag => ({ label: tag.name, value: tag.id }))
        this.initializeForm();
      },
      error: err => console.error('Erreur lors du chargement des données:', err)
    })
  }

  protected updateStepFromForm(): void {
    if (!this.emailEventForm || !this.emailEventForm.valid) return;

    this.emailEventStep.emailEvents = this.events?.value || [];
    this.emailEventStep.templateId = this.template?.value || null;
    this.emailEventStep.tags = this.tags?.value || [];
    this.emailEventStep.link = this.link?.value || null;

    this.emailEventStep.description = this.generateReadableDescription(this.emailEventStep.emailEvents, this.emailEventStep.templateId );
  }

  protected isConfigurationValid(): boolean {
    return this.emailEventForm.valid;
  }

  private createForm(): void {
    this.emailEventForm = this.formBuilder.group({
      events: this.createFormControl([], [Validators.required]),
      template: this.createFormControl('', [Validators.required]),
      tags: this.createFormControl([]),
      link: this.createFormControl('')
    });

    if (!this.readOnlyMode) {
      this.emailEventForm.valueChanges.pipe(
        takeUntil(this.destroy$),
      ).subscribe(() => {
        if (!this.isInitializing) {
          this.markAsChanged();
        }
      });
    }
  }

  private setupFormSubscription(): void {
    this.events?.valueChanges.subscribe(events => {
      if (events?.includes('CLICKS')) {
        this.link.setValidators([Validators.required]);
      } else {
        this.link?.clearValidators();
      }
      this.link?.updateValueAndValidity();
    });
  }

  private loadAvailableEvents(): void {
    this.availableEvents = Object.values(EmailEventStepDTO.EmailEventsEnum).map(value => ({
      value: value,
      label: this.getEventLabel(value)
    }))
  }

  private getEventLabel(value: string){
    return this.stepDescriptionService.emailEventLabel(value);
  }

  private initializeForm() {
    this.isInitializing = true;

    const safeTags = (this.emailEventStep.tags || [])
      .map(tag => {
        const num = parseInt(tag);
        return !isNaN(num) && this.availableTags.some(t => t.value === num) ? num : null;
      })
      .filter(tag => tag !== null);

    this.emailEventForm.patchValue({
      events: this.emailEventStep.emailEvents || [],
      template: this.emailEventStep.templateId || '',
      tags: safeTags,
      link: this.emailEventStep.link || ''
    });

    this.isInitializing = false;
  }

  private generateReadableDescription(emailEvents: Array<EmailEventStepDTO.EmailEventsEnum>, templateId: string): string {
    const template = this.availableTemplates.find(email => email.value === templateId);
    return this.stepDescriptionService.emailEvent(emailEvents, template?.label || templateId);
  }
}
